import { useQuery } from '@tanstack/react-query'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import axios from 'axios'
import { Provider as JotaiProvider } from 'jotai'
import posthog from 'posthog-js'
import { PostHogErrorBoundary, PostHogProvider } from 'posthog-js/react'
import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'

import { querySession } from '@api/session/session.queries.ts'

// Import the generated route tree
import ErrorComponent from './components/router/error-component.tsx'
import NotFound from './components/router/not-found.tsx'
import { ThemeProvider } from './components/theme/theme-provider.tsx'
import './i18n/i18n.ts'
import * as TanStackQueryProvider from './integrations/tanstack-query/root-provider.tsx'
import reportWebVitals from './reportWebVitals.ts'
import { routeTree } from './routeTree.gen'
import { store } from './stores/store'
import './styles.css'

//  Posthog config
const options = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  defaults: '2025-11-30',
} as const

// Create a new router instance

const TanStackQueryProviderContext = TanStackQueryProvider.getContext()
const router = createRouter({
  routeTree,
  context: {
    ...TanStackQueryProviderContext,
    session: null,
  },
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultStructuralSharing: true,
  // No preload stale time, since preloading is handled by tanstack query.
  // see https://tanstack.com/router/latest/docs/framework/react/guide/preloading#preloading-with-external-libraries
  defaultPreloadStaleTime: 0,
  defaultErrorComponent: (props) => {
    if (import.meta.env.VITE_ENV !== 'development')
      posthog.capture('$exception', {
        message: props.error.message,
        stack: props.error.stack,
        source: 'router',
      })

    return <ErrorComponent {...props} />
  },
  defaultNotFoundComponent: NotFound,
})

// Enable cookies for cross-origin requests
axios.defaults.withCredentials = true

// redirect api calls to the correct domain
if (import.meta.env.VITE_ENV !== 'development')
  axios.interceptors.request.use(function (config) {
    if (config.url?.startsWith('/api')) {
      config.url = config.url.replace('/api', '')
      config.baseURL = import.meta.env.VITE_API_URL
    }

    return config
  })

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

function InnerApp() {
  const { data: session } = useQuery(querySession())

  const context = { session }

  if (import.meta.env.VITE_ENV === 'development')
    return <RouterProvider router={router} context={context} />

  return (
    <PostHogErrorBoundary>
      <RouterProvider router={router} context={context} />
    </PostHogErrorBoundary>
  )
}

const InnerPostHogProvider = ({ children }: { children: React.ReactNode }) => {
  if (import.meta.env.VITE_ENV === 'development') return children

  return (
    <PostHogProvider
      apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
      options={options}
    >
      {children}
    </PostHogProvider>
  )
}

// Render the app
const rootElement = document.getElementById('app')
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <JotaiProvider store={store}>
        <InnerPostHogProvider>
          <TanStackQueryProvider.Provider {...TanStackQueryProviderContext}>
            <ThemeProvider defaultTheme="light">
              <InnerApp />
            </ThemeProvider>
          </TanStackQueryProvider.Provider>
        </InnerPostHogProvider>
      </JotaiProvider>
    </StrictMode>,
  )
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
